import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter} from '@computerrock/formation-router';
import {useTranslate} from '@computerrock/formation-i18n';
import {useStyles, Panel, DataRow, Checkbox, InputCurrency, Pill, DisplayCurrency} from '@ace-de/ui-components';
import {Icon, infoAlertIcon} from '@ace-de/ui-components/icons';
import * as serviceAssignmentSelectors from '../../service-assignments/serviceAssignmentSelectors';
import config from '../../config';

const CostCoverageCostOverviewPanel = props => {
    const {cx} = useStyles();
    const {createTranslateShorthand, activeLocale, translate} = useTranslate();
    const translateTab = createTranslateShorthand('sast_cost_coverage_tab');
    const {totalCost, costConfirmedByMember, onChangeHandler, serviceAssignment, hasCostError} = props;

    const isFullyCovered = totalCost - serviceAssignment.totalCoverage <= 0;

    return (
        <Panel
            title={(
                <div
                    className={cx([
                        'global!ace-u-flex',
                        'global!ace-u-flex--align-center',
                    ])}
                >
                    <p>
                        {translateTab('panel_title.cost_overview', {
                            priceType: Number(
                                serviceAssignment.serviceCase.commissioner.id,
                            ) !== config.ACE_COMMISSIONER_ID
                                ? translate('global.pricing_type.net')
                                : translate('global.pricing_type.gross'),
                        })}
                    </p>
                    {hasCostError && (
                        <div
                            className={cx([
                                'global!ace-u-flex',
                                'global!ace-u-flex--align-center',
                                'global!ace-u-margin--left-8',
                                'global!ace-u-typography--variant-caption',
                                'global!ace-u-typography--color-warning',
                            ])}
                        >
                            <Icon
                                icon={infoAlertIcon}
                                className={cx([
                                    'global!ace-u-margin--right-4',
                                    'ace-c-icon--s',
                                    'ace-c-icon--color-warning',
                                    'global!ace-u-flex--shrink-0',
                                ])}
                            />
                            {translateTab('panel_error.zero_cost_not_allowed')}
                        </div>
                    )}
                </div>
            )}
            qaIdent="sast-cost-coverage-cost-overview"
        >
            <div className={cx('global!ace-u-two-column-layout')}>
                <div>
                    <DataRow
                        label={translateTab('data_row_label.total_costs', {
                            priceType: Number(serviceAssignment.serviceCase.commissioner.id)
                            !== config.ACE_COMMISSIONER_ID
                                ? translate('global.pricing_type.net')
                                : translate('global.pricing_type.gross'),
                        })}
                        className={cx('ace-c-data-row--condensed')}
                        qaIdent="sa-total-cost"
                    >
                        <InputCurrency
                            className={cx('ace-c-input--small', 'global!ace-u-flex--grow-1')}
                            placeholder={translateTab('input_placeholder.please_enter_here')}
                            value={totalCost}
                            onChange={value => onChangeHandler('totalCost', value)}
                            min={0}
                            errors={hasCostError ? [''] : []}
                        />
                    </DataRow>
                    <DataRow
                        label={translateTab('data_row_label.maximum_budget', {
                            priceType: Number(serviceAssignment.serviceCase.commissioner.id)
                            !== config.ACE_COMMISSIONER_ID
                                ? translate('global.pricing_type.net')
                                : translate('global.pricing_type.gross'),
                        })}
                        qaIdent="sa-maximum-budget"
                    >
                        <DisplayCurrency
                            price={serviceAssignment.totalCoverage}
                            activeLocale={activeLocale}
                        />
                    </DataRow>
                    <DataRow
                        label={translateTab('data_row_label.difference')}
                        className={cx('ace-c-data-row--condensed')}
                        qaIdent="sa-cost-difference"
                    >
                        {totalCost && parseFloat(totalCost) ? (
                            // show pill only when agent enters totalCost
                            <Pill
                                type={isFullyCovered ? 'positive' : 'negative'}
                                className={cx('global!ace-u-typography--variant-body-medium')}
                            >
                                {isFullyCovered
                                    ? translateTab('difference_label.is_fully_covered')
                                    : (
                                        <>
                                            <DisplayCurrency
                                                price={totalCost - serviceAssignment.totalCoverage}
                                                activeLocale={activeLocale}
                                            />
                                            {translateTab('difference_label.add_payment')}
                                        </>
                                    )
                                }
                            </Pill>
                        ) : '-'}
                    </DataRow>
                </div>
                <div
                    className={cx([
                        'global!ace-u-flex',
                        'global!ace-u-flex--direction-column',
                        'global!ace-u-flex--justify-flex-end',
                    ])}
                >
                    {!isFullyCovered && (
                        <Checkbox
                            name="costConfirmedByMember"
                            value={true}
                            isSelected={!!costConfirmedByMember}
                            onChange={value => onChangeHandler('costConfirmedByMember', !!value)}
                            className={cx('global!ace-u-margin--bottom-16')}
                        >
                            {translateTab('checkbox_label.confirmed_by_member')}
                        </Checkbox>
                    )}
                </div>
            </div>
        </Panel>
    );
};

CostCoverageCostOverviewPanel.propTypes = {
    serviceAssignment: PropTypes.object,
    totalCost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // prop named differently to match DTO name
    // eslint-disable-next-line react/boolean-prop-naming
    costConfirmedByMember: PropTypes.bool,
    onChangeHandler: PropTypes.func,
    hasCostError: PropTypes.bool,
};

CostCoverageCostOverviewPanel.defaultProps = {
    serviceAssignment: {},
    totalCost: '',
    costConfirmedByMember: false,
    onChangeHandler: null,
    hasCostError: false,
};

const mapStateToProps = (state, props) => {
    const serviceAssignmentSelector = serviceAssignmentSelectors.createServiceAssignmentSelector();
    return {
        serviceAssignment: serviceAssignmentSelector(state, props),
    };
};

export default withRouter(connect(mapStateToProps, null)(CostCoverageCostOverviewPanel));
